/**
 * Ship modifications — non-stacking gear the player builds at the trade hub and
 * equips on the Carrier. For now the only kind is a Shield Generator, which adds
 * flat max-shield when equipped. Each mod rolls a rarity (Common → Legendary);
 * higher rarity + higher trade-hub tier give a bigger bonus (kept modest so even
 * a Legendary isn't overpowered).
 */
export type Rarity = "common" | "rare" | "epic" | "legendary";

export interface Modification {
  id: string; // unique instance id (non-stacking)
  kind: "shield";
  rarity: Rarity;
  shieldBonus: number; // flat +max shield when equipped
  regenMul: number; // shield-recharge-rate multiplier (higher rarity = faster)
  tier: number; // trade-hub mod tier it was built at
  level: number; // upgrade level (1 = fresh); raised at the mechanical yard
}

export const RARITY_META: Record<
  Rarity,
  { label: string; color: string }
> = {
  common: { label: "Common", color: "#5fe08a" },
  rare: { label: "Rare", color: "#4f9bff" },
  epic: { label: "Epic", color: "#b98cff" },
  legendary: { label: "Legendary", color: "#ff5a4a" },
};

// Base shield bonus by rarity. The default hull shield is small (~40), so these
// carry the shield capacity; higher rarity also recharges faster.
const BASE_SHIELD: Record<Rarity, number> = {
  common: 40,
  rare: 75,
  epic: 125,
  legendary: 190,
};
const REGEN_MUL: Record<Rarity, number> = {
  common: 1.1,
  rare: 1.3,
  epic: 1.6,
  legendary: 2.0,
};

/** Weighted rarity roll: Common 40%, Rare 35%, Epic 15%, Legendary 10%. */
export function rollRarity(rng: () => number = Math.random): Rarity {
  const r = rng() * 100;
  if (r < 40) return "common";
  if (r < 75) return "rare";
  if (r < 90) return "epic";
  return "legendary";
}

let modCounter = 0;

/** Build a fresh shield generator at the given trade-hub tier. */
export function makeShieldMod(tier: number, rng: () => number = Math.random): Modification {
  const rarity = rollRarity(rng);
  const scale = 1 + (tier - 1) * 0.25; // ~+25% per trade-hub tier
  const variance = 0.9 + rng() * 0.2;
  const shieldBonus = Math.max(1, Math.round(BASE_SHIELD[rarity] * scale * variance));
  modCounter += 1;
  return {
    id: `mod_${Date.now().toString(36)}_${modCounter}`,
    kind: "shield",
    rarity,
    shieldBonus,
    regenMul: REGEN_MUL[rarity],
    tier,
    level: 1,
  };
}

export function modName(m: Modification): string {
  return `${RARITY_META[m.rarity].label} Shield Generator`;
}
